import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { ArrowRight, Search, Package, AlertTriangle, Check, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Stock Adjustment Modal
 * 
 * Transfer stock from one product code to another at cost price.
 * Use case: Breaking down bulk items (e.g., roll of pipe into per-meter sales)
 * 
 * The total cost must balance: FROM (qty × cost) = TO (qty × cost)
 */
const StockAdjustmentModal = ({ open, onClose, onComplete }) => {
  // FROM product state
  const [fromSearch, setFromSearch] = useState("");
  const [fromSearchResults, setFromSearchResults] = useState([]);
  const [fromProduct, setFromProduct] = useState(null);
  const [fromQty, setFromQty] = useState(1);
  const [fromCost, setFromCost] = useState(0);

  // TO product state
  const [toSearch, setToSearch] = useState("");
  const [toSearchResults, setToSearchResults] = useState([]);
  const [toProduct, setToProduct] = useState(null);
  const [toQty, setToQty] = useState(1);
  const [toCost, setToCost] = useState(0);

  // UI state
  const [searching, setSearching] = useState({ from: false, to: false });
  const [processing, setProcessing] = useState(false);

  // Reset when modal opens
  useEffect(() => {
    if (open) {
      setFromSearch("");
      setFromSearchResults([]);
      setFromProduct(null);
      setFromQty(1);
      setFromCost(0);
      setToSearch("");
      setToSearchResults([]);
      setToProduct(null);
      setToQty(1);
      setToCost(0);
    }
  }, [open]);

  // Calculate totals
  const fromTotal = fromQty * fromCost;
  const toTotal = toQty * toCost;
  const isBalanced = Math.abs(fromTotal - toTotal) < 0.01;
  const difference = fromTotal - toTotal;

  // Search products
  const handleSearch = async (query, type) => {
    if (!query || query.length < 2) {
      if (type === 'from') setFromSearchResults([]);
      else setToSearchResults([]);
      return;
    }

    setSearching(prev => ({ ...prev, [type]: true }));
    try {
      const response = await axios.get(`${API}/products/search`, {
        params: { q: query }
      });
      if (type === 'from') setFromSearchResults(response.data.slice(0, 10));
      else setToSearchResults(response.data.slice(0, 10));
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setSearching(prev => ({ ...prev, [type]: false }));
    }
  };

  // Select FROM product
  const selectFromProduct = (product) => {
    setFromProduct(product);
    setFromSearch("");
    setFromSearchResults([]);
    setFromCost(product.last_cost_price_excl_vat || product.average_cost_price_excl_vat || product.cost_price || 0);
    setFromQty(1);
  };

  // Select TO product
  const selectToProduct = (product) => {
    setToProduct(product);
    setToSearch("");
    setToSearchResults([]);
    setToCost(product.last_cost_price_excl_vat || product.average_cost_price_excl_vat || product.cost_price || 0);
    // Auto-calculate quantity to balance if FROM is set
    if (fromProduct && fromTotal > 0 && toCost > 0) {
      const suggestedQty = Math.round((fromTotal / toCost) * 1000) / 1000;
      setToQty(suggestedQty);
    }
  };

  // Auto-balance TO quantity when FROM changes
  useEffect(() => {
    if (fromProduct && toProduct && toCost > 0) {
      const newFromTotal = fromQty * fromCost;
      const suggestedQty = Math.round((newFromTotal / toCost) * 1000) / 1000;
      if (suggestedQty > 0) {
        setToQty(suggestedQty);
      }
    }
  }, [fromQty, fromCost]);

  // Process the stock adjustment
  const handleSubmit = async () => {
    if (!fromProduct || !toProduct) {
      toast.error("Please select both FROM and TO products");
      return;
    }

    if (!isBalanced) {
      toast.error("Amounts must balance. Adjust quantities or costs.");
      return;
    }

    if (fromQty <= 0 || toQty <= 0) {
      toast.error("Quantities must be greater than 0");
      return;
    }

    if (fromProduct.current_stock < fromQty) {
      toast.error(`Insufficient stock. ${fromProduct.name} only has ${fromProduct.current_stock} available.`);
      return;
    }

    setProcessing(true);
    try {
      await axios.post(`${API}/products/stock-adjustment`, {
        from_product_id: fromProduct.id,
        from_qty: fromQty,
        from_cost: fromCost,
        to_product_id: toProduct.id,
        to_qty: toQty,
        to_cost: toCost,
        total_value: fromTotal,
        reason: `Stock transfer: ${fromProduct.plu_code} → ${toProduct.plu_code}`
      });

      toast.success(`Stock transferred successfully! ${fromProduct.plu_code} (-${fromQty}) → ${toProduct.plu_code} (+${toQty})`);
      
      if (onComplete) onComplete();
      onClose();
    } catch (error) {
      console.error("Stock adjustment error:", error);
      toast.error(error.response?.data?.detail || "Failed to process stock adjustment");
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (amount) => {
    return `R ${(amount || 0).toFixed(2)}`;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl p-0 overflow-hidden">
        <DialogHeader className="bg-gradient-to-r from-indigo-600 to-purple-600 p-4 text-white">
          <DialogTitle className="flex items-center gap-2 text-xl">
            <RefreshCw className="h-6 w-6" />
            Stock Adjustment - Transfer Between Codes
          </DialogTitle>
          <p className="text-indigo-100 text-sm">
            Transfer stock from one product code to another (e.g., break down rolls into per-meter)
          </p>
        </DialogHeader>

        <div className="p-6">
          {/* Two Column Layout */}
          <div className="grid grid-cols-2 gap-6">
            {/* FROM Section */}
            <div className="border-2 border-red-200 rounded-lg overflow-hidden">
              <div className="bg-red-500 text-white px-4 py-2 font-bold text-lg">
                FROM (Decrease Stock)
              </div>
              <div className="p-4 space-y-4">
                {/* Search */}
                <div>
                  <Label>Search Product</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <Input
                      value={fromSearch}
                      onChange={(e) => {
                        setFromSearch(e.target.value);
                        handleSearch(e.target.value, 'from');
                      }}
                      placeholder="PLU, barcode, or description..."
                      className="pl-9"
                      data-testid="from-search-input"
                    />
                  </div>
                  {/* Search Results */}
                  {fromSearchResults.length > 0 && (
                    <div className="mt-1 border rounded-lg max-h-40 overflow-y-auto">
                      {fromSearchResults.map(product => (
                        <button
                          key={product.id}
                          onClick={() => selectFromProduct(product)}
                          className="w-full text-left px-3 py-2 hover:bg-red-50 border-b last:border-b-0 text-sm"
                        >
                          <div className="font-medium">{product.name}</div>
                          <div className="text-gray-500 text-xs">
                            PLU: {product.plu_code} | Stock: {product.current_stock} | Cost: {formatCurrency(product.last_cost_price_excl_vat || product.cost_price)}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Selected Product */}
                {fromProduct ? (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="font-semibold text-red-800">{fromProduct.name}</div>
                    <div className="text-sm text-red-600">
                      PLU: {fromProduct.plu_code} | Current Stock: <span className="font-bold">{fromProduct.current_stock}</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setFromProduct(null)}
                      className="mt-2 text-red-600 hover:text-red-800 h-7 px-2"
                    >
                      Change Product
                    </Button>
                  </div>
                ) : (
                  <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center text-gray-400">
                    <Package className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p>Select a product to transfer FROM</p>
                  </div>
                )}

                {/* Quantity and Cost */}
                {fromProduct && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Quantity</Label>
                      <Input
                        type="number"
                        min="0.001"
                        step="0.001"
                        value={fromQty}
                        onChange={(e) => setFromQty(parseFloat(e.target.value) || 0)}
                        className="text-lg font-semibold"
                        data-testid="from-qty-input"
                      />
                    </div>
                    <div>
                      <Label>Cost Price (each)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={fromCost}
                        onChange={(e) => setFromCost(parseFloat(e.target.value) || 0)}
                        className="text-lg"
                        data-testid="from-cost-input"
                      />
                    </div>
                  </div>
                )}

                {/* Total */}
                {fromProduct && (
                  <div className="bg-red-100 rounded-lg p-3 text-center">
                    <div className="text-sm text-red-600">TOTAL VALUE</div>
                    <div className="text-2xl font-bold text-red-700">{formatCurrency(fromTotal)}</div>
                  </div>
                )}
              </div>
            </div>

            {/* TO Section */}
            <div className="border-2 border-green-200 rounded-lg overflow-hidden">
              <div className="bg-green-500 text-white px-4 py-2 font-bold text-lg">
                TO (Increase Stock)
              </div>
              <div className="p-4 space-y-4">
                {/* Search */}
                <div>
                  <Label>Search Product</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <Input
                      value={toSearch}
                      onChange={(e) => {
                        setToSearch(e.target.value);
                        handleSearch(e.target.value, 'to');
                      }}
                      placeholder="PLU, barcode, or description..."
                      className="pl-9"
                      data-testid="to-search-input"
                    />
                  </div>
                  {/* Search Results */}
                  {toSearchResults.length > 0 && (
                    <div className="mt-1 border rounded-lg max-h-40 overflow-y-auto">
                      {toSearchResults.map(product => (
                        <button
                          key={product.id}
                          onClick={() => selectToProduct(product)}
                          className="w-full text-left px-3 py-2 hover:bg-green-50 border-b last:border-b-0 text-sm"
                        >
                          <div className="font-medium">{product.name}</div>
                          <div className="text-gray-500 text-xs">
                            PLU: {product.plu_code} | Stock: {product.current_stock} | Cost: {formatCurrency(product.last_cost_price_excl_vat || product.cost_price)}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Selected Product */}
                {toProduct ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="font-semibold text-green-800">{toProduct.name}</div>
                    <div className="text-sm text-green-600">
                      PLU: {toProduct.plu_code} | Current Stock: <span className="font-bold">{toProduct.current_stock}</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setToProduct(null)}
                      className="mt-2 text-green-600 hover:text-green-800 h-7 px-2"
                    >
                      Change Product
                    </Button>
                  </div>
                ) : (
                  <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center text-gray-400">
                    <Package className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p>Select a product to transfer TO</p>
                  </div>
                )}

                {/* Quantity and Cost */}
                {toProduct && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Quantity</Label>
                      <Input
                        type="number"
                        min="0.001"
                        step="0.001"
                        value={toQty}
                        onChange={(e) => setToQty(parseFloat(e.target.value) || 0)}
                        className="text-lg font-semibold"
                        data-testid="to-qty-input"
                      />
                    </div>
                    <div>
                      <Label>Cost Price (each)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={toCost}
                        onChange={(e) => setToCost(parseFloat(e.target.value) || 0)}
                        className="text-lg"
                        data-testid="to-cost-input"
                      />
                    </div>
                  </div>
                )}

                {/* Total */}
                {toProduct && (
                  <div className="bg-green-100 rounded-lg p-3 text-center">
                    <div className="text-sm text-green-600">TOTAL VALUE</div>
                    <div className="text-2xl font-bold text-green-700">{formatCurrency(toTotal)}</div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Balance Indicator */}
          {fromProduct && toProduct && (
            <div className={`mt-6 p-4 rounded-lg flex items-center justify-center gap-4 ${
              isBalanced ? 'bg-green-100 border-2 border-green-300' : 'bg-yellow-100 border-2 border-yellow-300'
            }`}>
              <div className="text-center">
                <div className="text-red-600 font-semibold">{formatCurrency(fromTotal)}</div>
                <div className="text-xs text-gray-500">FROM</div>
              </div>
              
              <ArrowRight className={`h-8 w-8 ${isBalanced ? 'text-green-500' : 'text-yellow-500'}`} />
              
              <div className="text-center">
                <div className="text-green-600 font-semibold">{formatCurrency(toTotal)}</div>
                <div className="text-xs text-gray-500">TO</div>
              </div>

              <div className="ml-4 pl-4 border-l-2 border-gray-300">
                {isBalanced ? (
                  <div className="flex items-center gap-2 text-green-700">
                    <Check className="h-6 w-6" />
                    <span className="font-bold">BALANCED</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-yellow-700">
                    <AlertTriangle className="h-6 w-6" />
                    <div>
                      <div className="font-bold">NOT BALANCED</div>
                      <div className="text-sm">Difference: {formatCurrency(Math.abs(difference))}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="bg-gray-50 px-6 py-4 border-t">
          <Button variant="outline" onClick={onClose} disabled={processing}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!fromProduct || !toProduct || !isBalanced || processing}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="process-adjustment-button"
          >
            {processing ? "Processing..." : "Process Stock Adjustment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default StockAdjustmentModal;
